import { LuX } from 'react-icons/lu'
import { PiWifiHighBold, PiPowerBold, PiBellRingingBold, PiWarningBold } from 'react-icons/pi'

import { Button, Tooltip } from '@/components/ui'
import type { AssetState } from '@/types/asset-types'

import { formatDate } from './utils'

export interface AssetDetailHeaderProps {
  asset: AssetState
  /** Shows a close button; the modal leaves it out because the Modal shell has its own. */
  onClose?: () => void
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
  /** One short row, to fit the height of a table header row: counts lose their labels. */
  compact?: boolean
}

/** The asset's name, its active alarm/alert counts, and its gateway and power state. */
export default function AssetDetailHeader({ asset, onClose, tooltipPosition, compact }: AssetDetailHeaderProps) {
  const gatewayOn = asset.gatewayOn
  const powerStatus = asset.powerOn ? 'On' : 'Off'
  const iconSize = compact ? 'h-5 w-5' : 'h-6 w-6'
  const countClass = compact
    ? 'flex items-center gap-1'
    : 'py-1 px-2 flex flex-col md:flex-row justify-between items-center gap-2'

  return (
    <div className={`flex justify-between w-full items-center ${compact ? 'gap-3' : 'gap-4'}`}>
      <span className={compact ? 'font-semibold truncate' : undefined}>Asset: {asset.name || 'Loading...'}</span>
      <div className={`flex flex-row items-center text-xs shrink-0 ${compact ? 'gap-3' : 'gap-4 pr-4'}`}>
        <div className="flex items-center gap-2">
          {asset.activeAlarms.length > 0 && (
            <div className={countClass} title={compact ? 'Active alarms' : undefined}>
              <PiBellRingingBold className="text-primary-content h-4 w-4" />
              {compact ? asset.activeAlarms.length : `Active Alarms: ${asset.activeAlarms.length}`}
            </div>
          )}
          {asset.activeAlerts.length > 0 && (
            <div className={countClass} title={compact ? 'Active alerts' : undefined}>
              <PiWarningBold className="text-primary-content h-4 w-4" />
              {compact ? asset.activeAlerts.length : `Active Alerts: ${asset.activeAlerts.length}`}
            </div>
          )}
        </div>

        <div className={`flex justify-center items-center ${compact ? 'gap-2' : 'gap-4'}`}>
          <Tooltip
            position={tooltipPosition}
            tip={gatewayOn ? 'Online' : 'Offline'}
            children={<PiWifiHighBold className={`${iconSize} ${gatewayOn ? 'text-success' : 'text-zinc-400'}`} />}
          />
          <Tooltip
            position={tooltipPosition}
            tip={` Power ${powerStatus} Since ${formatDate(asset.powerAsOf)}`}
            children={<PiPowerBold className={`${iconSize} ${asset.powerOn ? 'text-success' : 'text-zinc-400'}`} />}
          />
          {onClose && <Button handleClick={onClose} icon={<LuX />} variation="ghost" size="xs" />}
        </div>
      </div>
    </div>
  )
}
